import { useContext, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';

import { GroupedTabs, Text } from '@/design-system';
import { useResizeObserver } from '@/utils/use-resize-observer';

import { useLangSmithTracingEnabled } from '../../hooks/useLangSmithTracingEnabled';
import {
  StudioRenderMode,
  StudioRenderModeContext,
} from '../../hooks/useStudioRenderMode';
import { LangSmithAwareContext } from '../../langsmith';
import { GraphThreadSelect } from '../site/GraphThreadSelect';
import { NewThreadButton } from '../site/NewThreadButton';
import { GraphSystemAssistantSelect } from './GraphAssistantSelect';
import { GraphChatModeToggle } from './GraphChatModeToggle';
import { BackButton, LanggraphApiOutdatedAlert } from './GraphTopBar';

export const StudioGraphControls = () => {
  const { standalone } = useOutletContext<{ standalone: boolean }>();
  const [searchParams] = useSearchParams();
  const hostProjectId = searchParams.get('hostProjectId');

  const [compactHeader, setCompactHeader] = useState(false);
  const headerRef = useResizeObserver<HTMLDivElement>((entry) => {
    const width = entry[0]?.contentRect.width;
    if (width && width < 420) {
      setCompactHeader(true);
    } else {
      setCompactHeader(false);
    }
  });

  return (
    <div
      ref={headerRef}
      className="flex h-[55px] w-full flex-shrink-0 items-center gap-2 overflow-x-hidden bg-primary p-2"
    >
      {!standalone && (
        <>
          <BackButton
            hostProjectId={hostProjectId}
            compactHeader={compactHeader}
          />
          /
        </>
      )}
      <div className="flex min-w-0 items-center gap-2">
        <GraphSystemAssistantSelect />
        <GraphChatModeToggle />
      </div>

      <LanggraphApiOutdatedAlert />
    </div>
  );
};

export const StudioThreadControls = () => {
  const { standalone } = useOutletContext<{ standalone: boolean }>();
  const { renderMode, setRenderMode } = useContext(StudioRenderModeContext);
  const { ExperimentModalAndButton } = useContext(LangSmithAwareContext);
  const { showLangSmithKeyWarning: tracingDisabled } =
    useLangSmithTracingEnabled();

  const [compactHeader, setCompactHeader] = useState(false);
  const headerRef = useResizeObserver<HTMLDivElement>((entry) => {
    const width = entry[0]?.contentRect.width;
    if (width && width < 400) {
      setCompactHeader(true);
    } else {
      setCompactHeader(false);
    }
  });

  return (
    <div
      ref={headerRef}
      className="flex h-[55px] w-full items-center justify-between gap-4 overflow-x-hidden p-2"
    >
      <div className="flex min-w-0 items-center gap-2">
        <GraphThreadSelect compactHeader={compactHeader} />
        <NewThreadButton />
      </div>
      <div className="ml-auto flex flex-shrink-0 grow items-center justify-end gap-2">
        {!standalone && (
          <GroupedTabs
            value={renderMode}
            onChange={(value) => setRenderMode(value)}
            options={[
              {
                display: 'Interact',
                value: StudioRenderMode.INTERACT,
                tooltip:
                  'Explore and engage with the thread. Navigate forks, view responses, and handle interrupts.',
              },
              {
                display: 'Trace',
                value: StudioRenderMode.TRACE,
                tooltip: (
                  <Text variant="sm">
                    Debug in more detail by inspecting execution traces.{' '}
                    {tracingDisabled && (
                      <>
                        Enable tracing via the{' '}
                        <Text
                          as="span"
                          className="font-mono text-secondary"
                          variant="sm"
                          weight="medium"
                        >
                          LANGSMITH_API_KEY
                        </Text>{' '}
                        environment variable to inspect traces.
                      </>
                    )}
                  </Text>
                ),
              },
            ]}
            size="md"
          />
        )}

        {ExperimentModalAndButton && !standalone && (
          <ExperimentModalAndButton />
        )}
      </div>
    </div>
  );
};
